import React, { useState, useEffect, useMemo } from 'react'
import { GoogleMap, Autocomplete, Marker } from '@react-google-maps/api'
import MiniLoading from '../MiniLoading'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import {
  getBuildingTypes,
  getPhoneTypes,
  UNIT_NUMBER_KEYS,
  UNIT_NUMBER_PLACEHOLDER_KEYS,
  showsFloorAndElevator,
} from 'Components/AddressForm/addressFormConstants'
import { BuildingTypeSelector } from 'Components/AddressForm/BuildingTypeSelector'
import { PhoneTypeSelector } from 'Components/AddressForm/PhoneTypeSelector'
import { DefaultAddressSection } from 'Components/AddressForm/DefaultAddressSection'
import { Switch } from '@mui/material'
import '../Map/mapa.css'
import { fetchAddressByGeolocation } from 'redux/actions/clientsActions'
import { useDispatch } from 'react-redux'
import {
  buildingTypeIds,
  isCountryPanama,
  telephoneTypeIds,
  DEFAULT_PHONE_PREFIX,
} from 'Utils/constants'
import styles from './styles.module.css'
import { CustomerContext } from 'state/context/CustomerFormContext'
import { t } from 'i18next'
import { PhoneNumberField } from 'Views/Clientes/CrearCliente/components/PhoneNumberField/PhoneNumberField'
import { useAddressCatalogs } from 'Hooks/useAddressCatalogs'

const COUNTRY_PH = 'PÁIS'
const PROVINCE_PH = 'PROVINCIA'
const DISTRICT_PH = 'DISTRITO'
const TOWNSHIP_PH = 'CORREGIMIENTO'

function NewCustomerMap({
  setHasAddress,
  hasAddress,
  countries = [],
  setCountryCode,
  setProvinceCode,
  setDistrictCode,
  setTownshipCode,
  street,
  setStreet,
  name,
  setName,
  mapKey,
  personType,
  formik,
}) {
  const mapContainerStyle = {
    height: '230px',
    width: '100%',
  }

  const dispatch = useDispatch()
  const { mandatoryAddress, setMandatoryAddress } = CustomerContext()
  const showAddress = personType !== 'NAT' ? true : mandatoryAddress

  // ---------------------------------------------------------------------------
  // Geographic dropdowns
  // ---------------------------------------------------------------------------
  const {
    provinces,
    districts,
    setDistricts,
    townships,
    setTownships,
    fetchProvinces,
    fetchDistrict,
    fetchTownship,
  } = useAddressCatalogs()
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_PH)
  const [selectedProvince, setSelectedProvince] = useState(PROVINCE_PH)
  const [selectedDistrict, setSelectedDistrict] = useState(DISTRICT_PH)
  const [selectedTownship, setSelectedTownship] = useState(TOWNSHIP_PH)
  const [showCountries, setShowCountries] = useState(false)
  const [showProvinces, setShowProvinces] = useState(false)
  const [showDistricts, setShowDistricts] = useState(false)
  const [showTownships, setShowTownships] = useState(false)
  const isPanama = isCountryPanama(formik?.values?.countryId)

  // ---------------------------------------------------------------------------
  // Map
  // ---------------------------------------------------------------------------
  const [lat, setLat] = useState(
    parseFloat(window.localStorage.getItem('lat')) || 8.98
  )
  const [lng, setLng] = useState(
    parseFloat(window.localStorage.getItem('long')) || -79.51
  )
  const [autocomplete, setAutocomplete] = useState(null)
  const [formIsLoading, setFormIsLoading] = useState(false)

  // ---------------------------------------------------------------------------
  // New address fields
  // ---------------------------------------------------------------------------
  const [neighborhood, setNeighborhood] = useState('')
  const [selectedBuildingTypeId, setSelectedBuildingTypeId] = useState(
    buildingTypeIds.casa
  )
  const [apartmentOrStoreNumber, setApartmentOrStoreNumber] = useState('')
  const [homeOrFloorNumber, setHomeOrFloorNumber] = useState('')
  const [hasElevator, setHasElevator] = useState(false)
  const [telephoneTypeId, setTelephoneTypeId] = useState(
    telephoneTypeIds.cellPhone
  )
  const [phoneData, setPhoneData] = useState({
    Prefix: DEFAULT_PHONE_PREFIX,
    number: '',
  })

  const handlePhoneChange = data => {
    setPhoneData(data)
    if (formik?.errors?.addressPhoneNumber) {
      formik.setFieldError('addressPhoneNumber', undefined)
    }
  }
  const [additionalInstructions, setAdditionalInstructions] = useState('')
  const [isDefaultDelivery, setIsDefaultDelivery] = useState(false)
  const [isDefaultBilling, setIsDefaultBilling] = useState(false)

  const isProyecto = selectedBuildingTypeId === buildingTypeIds.proyecto

  const buildingTypeLabel = useMemo(
    () =>
      getBuildingTypes().find(bt => bt.id === selectedBuildingTypeId)?.label ||
      '',
    [selectedBuildingTypeId]
  )

  const computedFinalAddress = useMemo(() => {
    const rest = [
      apartmentOrStoreNumber,
      neighborhood,
      selectedTownship !== TOWNSHIP_PH ? selectedTownship : '',
      selectedDistrict !== DISTRICT_PH ? selectedDistrict : '',
      selectedProvince !== PROVINCE_PH ? selectedProvince : '',
      selectedCountry !== COUNTRY_PH ? selectedCountry : '',
    ].filter(Boolean)

    return [buildingTypeLabel, rest.join(', ')]
      .filter(Boolean)
      .join(' – ')
      .substring(0, 99)
  }, [
    buildingTypeLabel,
    apartmentOrStoreNumber,
    neighborhood,
    selectedTownship,
    selectedDistrict,
    selectedProvince,
    selectedCountry,
  ])

  // Sync new fields into formik so CrearCliente can read them
  const syncFormikAddress = updates => {
    formik.setValues(prev => ({ ...prev, ...updates }))
  }

  // ---------------------------------------------------------------------------
  // Autocomplete / Map
  // ---------------------------------------------------------------------------
  const onLoad = autocom => setAutocomplete(autocom)

  const onPlaceChanged = () => {
    if (autocomplete !== null && autocomplete.getPlace().geometry) {
      setLat(autocomplete.getPlace().geometry.location.lat())
      setLng(autocomplete.getPlace().geometry.location.lng())
      fetchGenerals(
        autocomplete.getPlace().geometry.location.lat(),
        autocomplete.getPlace().geometry.location.lng()
      )
    } else {
      setSelectedCountry('')
      setCountryCode('')
      formik.setFieldValue('countryId', '')

      setSelectedProvince('')
      setProvinceCode('')
      formik.setFieldValue('provinceId', '')

      setSelectedDistrict('')
      setDistrictCode('')
      fetchDistrict('')
      formik.setFieldValue('districtId', '')

      setSelectedTownship('')
      setTownshipCode('')
      fetchTownship('')
      formik.setFieldValue('townShipId', '')

      setStreet('')
      formik.setFieldValue('addressStreet', '')
      setName('')
      setAdditionalInstructions('')
      formik.setFieldValue('addressDetail', '')
      formik.setFieldValue('additionalInstructions', '')

      setHasAddress({
        country: false,
        province: false,
        district: false,
        township: false,
        street: false,
        detail: true,
      })
    }
  }

  const handleMarker = e => {
    setLat(e.latLng.lat())
    setLng(e.latLng.lng())
    fetchGenerals(e.latLng.lat(), e.latLng.lng())
  }

  // ---------------------------------------------------------------------------
  // Geographic handlers
  // ---------------------------------------------------------------------------
  const handleCountry = e => {
    const trimId = e.target.id.slice(0, -3)
    setSelectedCountry(e.target.value)
    setCountryCode(trimId)
    setHasAddress({ ...hasAddress, country: true, province: false })
    setShowCountries(false)
    formik.setFieldValue('countryId', trimId)

    fetchProvinces(trimId)
    setSelectedProvince(PROVINCE_PH)
    setSelectedDistrict(DISTRICT_PH)
    setSelectedTownship(TOWNSHIP_PH)
    setDistricts([])
    setTownships([])
    formik.setFieldValue('provinceId', null)
    formik.setFieldValue('districtId', null)
    formik.setFieldValue('townShipId', null)
  }

  const handleProvince = e => {
    const trimId = e.target.id.slice(0, -3)
    setSelectedProvince(e.target.value)
    setProvinceCode(trimId)
    setShowProvinces(false)
    fetchDistrict(trimId)
    setHasAddress({ ...hasAddress, province: true })
    setSelectedDistrict('DISTRITO')
    setSelectedTownship('CORREGIMIENTO')
    formik.setFieldValue('districtId', null)
    formik.setFieldValue('townShipId', null)
    formik.setFieldValue('provinceId', trimId)
  }

  const handleDistrict = e => {
    const trimId = e.target.id.slice(0, -3)
    setSelectedDistrict(e.target.value)
    setDistrictCode(trimId)
    setShowDistricts(false)
    fetchTownship(trimId)
    setHasAddress({ ...hasAddress, district: true })
    setSelectedTownship('CORREGIMIENTO')
    formik.setFieldValue('townShipId', null)
    formik.setFieldValue('districtId', trimId)
  }

  const handleTownship = e => {
    const trimId = e.target.id.slice(0, -3)
    setSelectedTownship(e.target.value)
    setTownshipCode(trimId)
    setHasAddress({ ...hasAddress, township: true })
    setShowTownships(false)
    formik.setFieldValue('townShipId', trimId)
  }

  // ---------------------------------------------------------------------------
  // Location endpoints
  // ---------------------------------------------------------------------------
  const fetchGenerals = async (latitud, longitude) => {
    setFormIsLoading(true)
    setHasAddress({
      ...hasAddress,
      country: false,
      province: false,
      district: false,
      township: false,
    })
    formik.setFieldValue('countryId', null)
    formik.setFieldValue('provinceId', null)
    formik.setFieldValue('districtId', null)
    formik.setFieldValue('townShipId', null)
    formik.setFieldValue('latitude', null)
    formik.setFieldValue('longitude', null)
    const payload = {
      Latitude: latitud,
      Longitude: longitude,
    }
    const Data = await dispatch(fetchAddressByGeolocation(payload))
    if (Array.isArray(Data) && Data.length > 0) {
      const loc = Data[0]
      formik.setFieldValue('latitude', latitud)
      formik.setFieldValue('longitude', longitude)

      setSelectedCountry(loc.Country)
      setCountryCode(loc.CountryId)
      formik.setFieldValue('countryId', loc.CountryId)
      fetchProvinces(loc.CountryId)

      setSelectedProvince(loc.Province)
      setProvinceCode(loc.ProvinceId)
      formik.setFieldValue('provinceId', loc.ProvinceId)

      setSelectedDistrict(loc.District)
      setDistrictCode(loc.DistrictId)
      fetchDistrict(loc.ProvinceId)
      formik.setFieldValue('districtId', loc.DistrictId)

      setSelectedTownship(loc.Township)
      setTownshipCode(loc.TownshipId)
      fetchTownship(loc.DistrictId)
      formik.setFieldValue('townShipId', loc.TownshipId)

      setHasAddress({
        ...hasAddress,
        country: true,
        province: true,
        district: true,
        township: true,
      })
    } else if (!Array.isArray(Data)) {
      console.error('Error getting Generals: ', Data)
    }
    setFormIsLoading(false)
  }

  useEffect(() => {
    fetchProvinces()
  }, [mapKey])

  // Sync new fields into formik whenever they change
  useEffect(() => {
    syncFormikAddress({
      neighborhoodName: neighborhood,
      buildingTypeId: selectedBuildingTypeId,
      apartmentOrStoreNumber: apartmentOrStoreNumber || null,
      homeOrFloorNumber: showsFloorAndElevator(selectedBuildingTypeId)
        ? homeOrFloorNumber || null
        : null,
      hasElevator: showsFloorAndElevator(selectedBuildingTypeId)
        ? hasElevator
        : false,
      telephoneTypeId,
      phonePrefix: phoneData.Prefix,
      addressPhoneNumber: phoneData.number || null,
      addressPhoneCountryCode: phoneData.countryCode || 'PA',
      additionalInstructions: additionalInstructions || null,
      isDefaultDeliveryAddress: isDefaultDelivery,
      isDefaultBillingAddress: isDefaultBilling,
      addressComputedFull: computedFinalAddress,
    })
  }, [
    neighborhood,
    selectedBuildingTypeId,
    apartmentOrStoreNumber,
    homeOrFloorNumber,
    hasElevator,
    telephoneTypeId,
    phoneData,
    additionalInstructions,
    isDefaultDelivery,
    isDefaultBilling,
    computedFinalAddress,
  ])

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div className={styles.formAddressContainer}>
      {personType === 'NAT' && (
        <div className="address-requiered-question-wrapper">
          <p>{t('ADDRESS_FORM_T.WANT_ADDRESS_QUESTION')}</p>
          <div className="address-checkbox-wrapper">
            <div className="custom-radio">
              <input
                type="radio"
                id="addressCheckboxYes"
                checked={mandatoryAddress}
                data-testid="input-yes-address"
                onChange={() => setMandatoryAddress(true)}
              />
              <label
                className="custom-control-label"
                htmlFor="addressCheckboxYes">
                {t('ADDRESS_FORM_T.YES_OPTION')}
              </label>
            </div>
            <div className="custom-control custom-radio">
              <input
                type="radio"
                id="addressCheckboxNo"
                checked={!mandatoryAddress}
                data-testid="input-no-address"
                onChange={() => setMandatoryAddress(false)}
              />
              <label
                className="custom-control-label"
                htmlFor="addressCheckboxNo">
                {t('ADDRESS_FORM_T.NO_OPTION')}
              </label>
            </div>
          </div>
        </div>
      )}

      <div className={showAddress ? '' : styles.hidden}>
        {/* Google Autocomplete */}
        <div className={styles.inputContainer}>
          <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
            <input
              type="text"
              name="addressMap"
              placeholder={t('ADDRESS_FORM_T.SEARCH_PLACEHOLDER')}
              autoComplete="off"
              className={styles.inputMapAddress}
              onKeyDown={e => e.key !== 'Enter'}
              data-testid="input-map-address"
            />
          </Autocomplete>
        </div>

        {/* Map */}
        <GoogleMap
          id="search-map-build"
          mapContainerStyle={mapContainerStyle}
          zoom={15}
          onClick={handleMarker}
          options={{
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            zoomControl: true,
            disableDoubleClickZoom: true,
          }}
          center={{ lat, lng }}>
          <Marker position={{ lat, lng }} />
        </GoogleMap>

        {!formIsLoading ? (
          <form>
            {/* País / Provincia */}
            <div className="form-group-select">
              <div className={styles.selecContainer}>
                <div
                  className={`address-search-filter${
                    showCountries ? ' address-search-filter-open' : ''
                  }`}>
                  <button
                    type="button"
                    className="address-filter-content"
                    data-testid="dropdown-country"
                    disabled={isPanama}
                    onClick={() => setShowCountries(!showCountries)}>
                    <h2>{`${selectedCountry}*`}</h2>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <ul
                    className={
                      showCountries
                        ? 'address-filter-options'
                        : 'address-filter-options-hidden'
                    }>
                    {countries.length === 0 && (
                      <li className="address-filter-option address-filter-option-empty">
                        {t('ADDRESS_FORM_T.NO_OPTIONS_AVAILABLE')}
                      </li>
                    )}
                    {countries.map(country => (
                      <li
                        className="address-filter-option"
                        data-testid={`country-option-${country.CountryId}`}
                        key={country.CountryId + personType}>
                        <input
                          type="radio"
                          className="radio"
                          id={country.CountryId + personType}
                          name="pais"
                          value={country.CountryName}
                          onClick={handleCountry}
                          disabled={isPanama}
                        />
                        <label htmlFor={country.CountryId + personType}>
                          {country.CountryName}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles.errorMessage}>
                  {formik?.errors?.countryId && (
                    <strong>{formik.errors.countryId}</strong>
                  )}
                </div>
              </div>

              <div className={styles.selecContainer}>
                <div
                  className={`address-search-filter${
                    showProvinces ? ' address-search-filter-open' : ''
                  }`}>
                  <button
                    type="button"
                    className="address-filter-content"
                    data-testid="dropdown-province"
                    disabled={isPanama && Boolean(formik?.values?.provinceId)}
                    onClick={() => setShowProvinces(!showProvinces)}>
                    <h2>{`${selectedProvince}*`}</h2>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <ul
                    className={
                      showProvinces
                        ? 'address-filter-options'
                        : 'address-filter-options-hidden'
                    }>
                    {provinces.length === 0 && (
                      <li className="address-filter-option address-filter-option-empty">
                        {t('ADDRESS_FORM_T.NO_OPTIONS_AVAILABLE')}
                      </li>
                    )}
                    {provinces.map(province => (
                      <li
                        className="address-filter-option"
                        data-testid={`province-option-${province.ProvinceId}`}
                        key={province.ProvinceId + personType}>
                        <input
                          type="radio"
                          className="radio"
                          id={province.ProvinceId + personType}
                          name="provincia"
                          value={province.Name}
                          onClick={handleProvince}
                          disabled={
                            isPanama && Boolean(formik?.values?.provinceId)
                          }
                        />
                        <label htmlFor={province.ProvinceId + personType}>
                          {province.Name}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles.errorMessage}>
                  {formik?.errors?.provinceId && (
                    <strong>{formik.errors.provinceId}</strong>
                  )}
                </div>
              </div>
            </div>

            {/* Distrito / Corregimiento */}
            <div className="form-group-select">
              <div className={styles.selecContainer}>
                <div
                  className={`address-search-filter${
                    showDistricts ? ' address-search-filter-open' : ''
                  }`}>
                  <button
                    type="button"
                    className="address-filter-content"
                    data-testid="dropdown-district"
                    disabled={isPanama && Boolean(formik?.values?.districtId)}
                    onClick={() => setShowDistricts(!showDistricts)}>
                    <h2>{`${selectedDistrict}*`}</h2>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <ul
                    className={
                      showDistricts
                        ? 'address-filter-options'
                        : 'address-filter-options-hidden'
                    }>
                    {districts.length === 0 && (
                      <li className="address-filter-option address-filter-option-empty">
                        {t('ADDRESS_FORM_T.NO_OPTIONS_AVAILABLE')}
                      </li>
                    )}
                    {districts.map(district => (
                      <li
                        className="address-filter-option"
                        data-testid={`district-option-${district.DistrictId}`}
                        key={district.DistrictId + personType}>
                        <input
                          type="radio"
                          className="radio"
                          id={district.DistrictId + personType}
                          name="distrito"
                          value={district.Name}
                          onClick={handleDistrict}
                          disabled={
                            isPanama && Boolean(formik?.values?.districtId)
                          }
                        />
                        <label htmlFor={district.DistrictId + personType}>
                          {district.Name}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles.errorMessage}>
                  {formik?.errors?.districtId && (
                    <strong>{formik.errors.districtId}</strong>
                  )}
                </div>
              </div>

              <div className={styles.selecContainer}>
                <div
                  className={`address-search-filter${
                    showTownships ? ' address-search-filter-open' : ''
                  }`}>
                  <button
                    type="button"
                    className="address-filter-content"
                    data-testid="dropdown-township"
                    disabled={isPanama && Boolean(formik?.values?.townShipId)}
                    onClick={() => setShowTownships(!showTownships)}>
                    <h2>{`${selectedTownship}*`}</h2>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <ul
                    className={
                      showTownships
                        ? 'address-filter-options'
                        : 'address-filter-options-hidden'
                    }>
                    {townships.length === 0 && (
                      <li className="address-filter-option address-filter-option-empty">
                        {t('ADDRESS_FORM_T.NO_OPTIONS_AVAILABLE')}
                      </li>
                    )}
                    {townships.map(township => (
                      <li
                        className="address-filter-option"
                        data-testid={`township-option-${township.TownshipId}`}
                        key={township.TownshipId + personType}>
                        <input
                          type="radio"
                          className="radio"
                          id={township.TownshipId + personType}
                          name="corregimiento"
                          value={township.Name}
                          onClick={handleTownship}
                          disabled={
                            isPanama && Boolean(formik?.values?.townShipId)
                          }
                        />
                        <label htmlFor={township.TownshipId + personType}>
                          {township.Name}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles.errorMessage}>
                  {formik?.errors?.townShipId && (
                    <strong>{formik.errors.townShipId}</strong>
                  )}
                </div>
              </div>
            </div>

            {/* Tipo de Vivienda */}
            <div className="address-section">
              <h3 className="address-section-title">
                {t('ADDRESS_FORM_T.BUILDING_TYPE_TITLE')}
              </h3>
              <BuildingTypeSelector
                types={getBuildingTypes()}
                selectedId={selectedBuildingTypeId}
                onSelect={setSelectedBuildingTypeId}
              />
            </div>

            {/* Número de unidad + Vecindario */}
            <div className="form-group-select">
              {!isProyecto && (
                <div className={styles.inputContainer}>
                  <label className="address-input-label">
                    {t(
                      UNIT_NUMBER_KEYS[selectedBuildingTypeId] ||
                        'ADDRESS_FORM_T.UNIT_NUMBER_CASA'
                    )}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t(
                      UNIT_NUMBER_PLACEHOLDER_KEYS[selectedBuildingTypeId] ||
                        'ADDRESS_FORM_T.PLACEHOLDER_UNIT_NUMBER_CASA'
                    )}
                    maxLength={10}
                    data-testid="input-unit-number"
                    value={apartmentOrStoreNumber}
                    onChange={e => {
                      setApartmentOrStoreNumber(
                        e.target.value.replace(/^\s+/, '')
                      )
                      setHasAddress({
                        ...hasAddress,
                        street: e.target.value.length >= 1,
                      })
                    }}
                  />
                </div>
              )}
              <div className={styles.inputContainer}>
                <label className="address-input-label">
                  {t('ADDRESS_FORM_T.LABEL_NEIGHBORHOOD')}
                  <span className="required">*</span>
                </label>
                <input
                  type="text"
                  placeholder={t('ADDRESS_FORM_T.PLACEHOLDER_NEIGHBORHOOD')}
                  maxLength={50}
                  data-testid="input-neighborhood"
                  value={neighborhood}
                  onChange={e => {
                    const val = e.target.value.replace(/^\s+/, '')
                    setNeighborhood(val)
                    setStreet(val)
                    formik.setFieldValue('addressStreet', val)
                    setHasAddress({
                      ...hasAddress,
                      street: val.length >= 1,
                    })
                  }}
                />
                <div className={styles.inputFooter}>
                  <div className={styles.errorMessage}>
                    {formik?.errors?.addressStreet && (
                      <strong>{formik.errors.addressStreet}</strong>
                    )}
                  </div>
                  <p className="char-counter" style={{ marginTop: 0 }}>
                    {t('ADDRESS_FORM_T.CHARS_REMAINING', {
                      count: 50 - neighborhood.length,
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Piso + Elevador (solo para Apartamento y Centro Comercial) */}
            {showsFloorAndElevator(selectedBuildingTypeId) && (
              <>
                <div className={styles.inputContainer}>
                  <label className="address-input-label">
                    {t('ADDRESS_FORM_T.LABEL_FLOOR')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('ADDRESS_FORM_T.PLACEHOLDER_FLOOR')}
                    maxLength={10}
                    data-testid="input-floor-number"
                    value={homeOrFloorNumber}
                    onChange={e =>
                      setHomeOrFloorNumber(e.target.value.replace(/^\s+/, ''))
                    }
                  />
                </div>
                <div className="elevator-toggle-row">
                  <span className="elevator-label">
                    {t('ADDRESS_FORM_T.ELEVATOR_LABEL')}
                  </span>
                  <Switch
                    checked={hasElevator}
                    onChange={e => setHasElevator(e.target.checked)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#003082',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        {
                          backgroundColor: '#003082',
                        },
                    }}
                  />
                </div>
              </>
            )}

            {/* Contacto & Alias */}
            <div className="address-section" style={{ marginTop: '20px' }}>
              <h3 className="address-section-title">
                {t('ADDRESS_FORM_T.CONTACT_SECTION_TITLE')}
              </h3>

              <PhoneTypeSelector
                types={getPhoneTypes()}
                selectedId={telephoneTypeId}
                onSelect={setTelephoneTypeId}
              />

              {/* Phone prefix + number | Alias */}
              <div className="form-group-select" style={{ marginTop: '12px' }}>
                <div className={styles.inputContainer}>
                  <label className="address-input-label">
                    {t('ADDRESS_FORM_T.LABEL_PHONE')}
                    <span className="required">*</span>
                  </label>
                  <PhoneNumberField
                    value={phoneData}
                    onChange={handlePhoneChange}
                  />
                  <p className={styles.errorMessage}>
                    {formik?.errors?.addressPhoneNumber || ''}
                  </p>
                </div>

                <div className={styles.inputContainer}>
                  <label className="address-input-label">
                    {t('ADDRESS_FORM_T.LABEL_ALIAS')}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={t('ADDRESS_FORM_T.PLACEHOLDER_ALIAS')}
                    maxLength={30}
                    minLength={5}
                    name="addressDetail"
                    value={name}
                    data-testid="input-detail-address"
                    onChange={e => {
                      setName(e.target.value)
                      formik.setFieldValue('addressDetail', e.target.value)
                      setHasAddress({
                        ...hasAddress,
                        detail: e.target.value.length >= 1,
                      })
                    }}
                  />
                  <div className={styles.inputFooter}>
                    <p className={styles.errorMessage}>
                      {formik?.errors?.addressDetail || ''}
                    </p>
                    <p className="char-counter" style={{ marginTop: 0 }}>
                      {t('ADDRESS_FORM_T.CHARS_REMAINING', {
                        count: 30 - name.length,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Instrucciones adicionales */}
              <div
                className={styles.inputContainer}
                style={{ marginTop: '12px' }}>
                <textarea
                  className="additional-instructions-input"
                  placeholder={t(
                    'ADDRESS_FORM_T.ADDITIONAL_INSTRUCTIONS_PLACEHOLDER'
                  )}
                  maxLength={255}
                  rows={3}
                  value={additionalInstructions}
                  onChange={e => setAdditionalInstructions(e.target.value)}
                />
                <p className="char-counter">
                  {additionalInstructions.length}/255
                </p>
              </div>
            </div>

            {/* Establecer Dirección por Defecto */}
            <DefaultAddressSection
              isDefaultDelivery={isDefaultDelivery}
              onDeliveryChange={setIsDefaultDelivery}
              isDefaultBilling={isDefaultBilling}
              onBillingChange={setIsDefaultBilling}
            />
          </form>
        ) : (
          <MiniLoading />
        )}
      </div>
    </div>
  )
}

export default NewCustomerMap
